import * as React from 'react'
import { useNavigate, useOutletContext } from 'react-router-dom'

import {
  ContractVersionsPageDocument,
  WorkingConditionVersionApplicationFragment,
} from 'generated/resources/graphql'
import moment from 'moment'

import { observer } from 'mobx-react'

import { useEffect } from 'modules/core/hooks/useEffect'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'

import BlankSlate from 'modules/contracts/components/BlankSlate'
import ContractDetails from 'modules/contracts/components/ContractDetails'
import { useContractVersions } from 'modules/contracts/lib/contractVersionsContext'
import {
  filterContractVersionsByCurrent,
  getUpcomingContractVersions,
} from 'modules/contracts/lib/utils'
import type ContractVersion from 'modules/contracts/store/models/ContractVersion'
import type Employee from 'modules/employees/store/models/Employee'

interface Props {
  employee: Employee
  contractVersion: ContractVersion | null | undefined
  workingConditionVersionApplication?: WorkingConditionVersionApplicationFragment
}

const ContractVersionsContent = observer(
  ({ employee, contractVersion, workingConditionVersionApplication }: Props) => {
    const contractVersions = useContractVersions()

    const getContractVersion = () => {
      const today = moment()
      if (contractVersion) return contractVersion

      const current = filterContractVersionsByCurrent(contractVersions.models, today)[0]

      if (current) return current

      return getUpcomingContractVersions(contractVersions.models, today)
    }

    const [selectedContractVersion, setSelectedContractVersion] = React.useState(
      getContractVersion()
    )
    if (!selectedContractVersion) return <BlankSlate employee={employee} />

    return (
      <ContractDetails
        employee={employee}
        setSelectedContractVersion={setSelectedContractVersion}
        selectedContractVersion={selectedContractVersion}
        workingConditionVersionApplication={workingConditionVersionApplication}
      />
    )
  }
)

const ContractVersionsPage = () => {
  const { employee, contractVersion } = useOutletContext<{
    employee: Employee
    contractVersion: ContractVersion | null | undefined
  }>()
  const employeeId = employee.id
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const { data, loading } = useQuery(ContractVersionsPageDocument, {
    variables: {
      employeeId,
      contractVersionId: contractVersion?.id || 0,
      fetchWorkingConditions: !!contractVersion,
    },
  })
  const canSeeContract = data?.employees.employee?._policies?.seeContract.can ?? false
  const workingConditionVersionApplication =
    data?.workingConditions?.versionApplicationsConnection?.nodes[0]

  useEffect(() => {
    if (!loading && !canSeeContract) {
      navigate(navigation.employees.id.fullPath({ id: employeeId }))
    }
  }, [navigate, loading, navigation, employeeId, canSeeContract])

  if (!canSeeContract) {
    return null
  }

  return (
    <ContractVersionsContent
      employee={employee}
      contractVersion={contractVersion}
      workingConditionVersionApplication={workingConditionVersionApplication}
    />
  )
}

export default observer(ContractVersionsPage)
